import { ReactNode, useCallback, useEffect, useRef } from 'react'
import { Sidebar } from './Sidebar'
import { Topbar } from './Topbar'
import { AiChatPanel } from './AiChatPanel'
import { AiChatPanelProvider } from '@/contexts/AiChatPanelContext'
import { useMe } from '@/features/auth/hooks/useAuth'
import { userHasPermission } from '@/lib/permissions'

interface AppShellProps {
    children: ReactNode
}

export function AppShell({ children }: AppShellProps) {

    // Fetch complete user data including avatarUrl
    const { data: me } = useMe()
    const canUseAi = userHasPermission(me, 'ai.use')

    // Feather the top/bottom edges of the scrolling content area — but only when there's more
    // content that way (a crisp edge = you're at the very top / bottom). This is scroll-aware and
    // NEVER bounds the content into a box (it keeps using the full screen). Opacity is written
    // directly to the overlay DOM in the scroll handler so scrolling doesn't re-render the app.
    const mainRef = useRef<HTMLElement>(null)
    const contentRef = useRef<HTMLDivElement>(null)
    const topRef = useRef<HTMLDivElement>(null)
    const bottomRef = useRef<HTMLDivElement>(null)

    const recompute = useCallback(() => {
        const el = mainRef.current
        if (!el || !topRef.current || !bottomRef.current) return
        topRef.current.style.opacity = el.scrollTop > 4 ? '1' : '0'
        bottomRef.current.style.opacity = el.scrollTop + el.clientHeight < el.scrollHeight - 4 ? '1' : '0'
    }, [])

    useEffect(() => {
        const el = mainRef.current
        if (!el) return
        recompute()
        el.addEventListener('scroll', recompute, { passive: true })
        window.addEventListener('resize', recompute)
        // Re-evaluate when the page content changes size (navigation, async loads, expanding rows).
        const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(recompute) : null
        if (ro) {
            ro.observe(el)
            if (contentRef.current) ro.observe(contentRef.current)
        }
        return () => {
            el.removeEventListener('scroll', recompute)
            window.removeEventListener('resize', recompute)
            ro?.disconnect()
        }
    }, [recompute])

    return (
        <AiChatPanelProvider>
            <div className="flex h-screen bg-background">
                <Sidebar />
                <div className="flex flex-1 flex-col overflow-hidden bg-card">
                    <Topbar />
                    <div className="relative flex-1 overflow-hidden">
                        <main ref={mainRef} className="h-full overflow-auto bg-background">
                            <div ref={contentRef} className="container mx-auto p-4">
                                {children}
                            </div>
                        </main>
                        {/* Scroll-edge feathers — fade in only when there's more content above / below. */}
                        <div
                            ref={topRef}
                            aria-hidden
                            className="pointer-events-none absolute inset-x-0 top-0 z-20 h-14 opacity-0 transition-opacity duration-150"
                            style={{ background: 'linear-gradient(to bottom, hsl(var(--background)), transparent)' }}
                        />
                        <div
                            ref={bottomRef}
                            aria-hidden
                            className="pointer-events-none absolute inset-x-0 bottom-0 z-20 h-14 opacity-0 transition-opacity duration-150"
                            style={{ background: 'linear-gradient(to top, hsl(var(--background)), transparent)' }}
                        />
                    </div>
                </div>
                {/* Clara and User Chat both read /api/ai/*, which needs ai.use (docs/rbac-permissions.md). */}
                {canUseAi && <AiChatPanel />}
            </div>
        </AiChatPanelProvider>
    )
}
